import {
  Country,
  CountryEnum,
  OrderTransactionStore,
  type OrderTransactionStoreValues,
  OrderTransactionStoreMix,
  type OrderTransactionStoreMixValues,
  PartnerErp,
  type PartnerErpValues,
  PartnerSource,
  RecordOrigin,
  type RecordOriginValues,
} from '@sr/types';

import { getCountryFromDwKey, isConfidential, isDwkeyInCountry, isDwkeyOp } from '../country';

/**
 * The fields needed to answer which ERP a Partner transacts against. Kept structural so
 * the backend Partner, the partner API response and the frontend location type all
 * satisfy it without adapters.
 */
export type PartnerErpInput = {
  source: string;
  dwKey: string;
};

export type PartnerErpOptions = {
  /** Whether every Swedish Partner has moved to FNO. */
  isFnoLive: boolean;
  /** Whether the Partner belongs to a region that has already moved to FNO. */
  isPartnerPilot: boolean;
};

/** Whether a Partner transacts against FNO, as resolved by the partner API. */
export const isFnoPartner = (partner: { erp?: PartnerErpValues } | null | undefined): boolean =>
  partner?.erp === PartnerErp.FNO;

/** Whether a Partner belongs to Stena Confidential. */
export const isCsPartner = (partner: Pick<PartnerErpInput, 'source'>): boolean =>
  partner.source === PartnerSource.CS;

/**
 * The business system a Partner transacts against.
 *
 * Swedish partners are the only ones whose answer is not a direct read: they move from
 * Fire to FNO region by region, and the caller has already resolved which side of that
 * move the Partner is on. The stored `source` says where the record came from, not
 * which ERP it transacts against, so it never decides here.
 */
export const getPartnerErp = (
  partner: PartnerErpInput,
  { isFnoLive, isPartnerPilot }: PartnerErpOptions,
): PartnerErpValues => {
  if (isCsPartner(partner)) {
    return PartnerErp.CS;
  }

  if (!isDwkeyInCountry(partner.dwKey, CountryEnum.SE)) {
    return PartnerErp.NAVISION;
  }

  if (isFnoLive || isPartnerPilot) {
    return PartnerErp.FNO;
  }

  return PartnerErp.FIRE;
};

/**
 * The upstream system a Partner record itself came from. Datalake records are keyed by
 * dwKey and carry a different field shape from warehouse records, whichever ERP the
 * Partner transacts against.
 */
export const getPartnerRecordOrigin = (
  partner: Pick<PartnerErpInput, 'source'>,
): RecordOriginValues => {
  switch (partner.source) {
    case PartnerSource.CS:
      return RecordOrigin.CS;
    case PartnerSource.FNO:
    case PartnerSource['DL-FIRE']:
    case PartnerSource.DL_FI:
      return RecordOrigin.DL;
    default:
      return RecordOrigin.EDW;
  }
};

/**
 * Where a Partner's aggregated order-transaction rows live. Decided by the dwKey, not the
 * ERP. Swedish, Datalake Finnish and Confidential partners are in Operational Storage,
 * everyone else in EDW.
 */
export const getOrderTransactionStore = (dwKey: string): OrderTransactionStoreValues => {
  if (isDwkeyOp(dwKey)) {
    return OrderTransactionStore.OP;
  }

  return OrderTransactionStore.EDW;
};

export type OrderTransactionStores = {
  mix: OrderTransactionStoreMixValues;
  edwDwKeys: string[];
  partnersCountry: Country;
};

/**
 * The stores a set of partners' order transactions span.
 *
 * Datalake OP partners' rows exist in both Operational Storage and EDW, so unioning the two
 * would double-count them. Mixing a Datalake OP partner with a warehouse-sourced one is
 * therefore refused outright; Confidential and warehouse-sourced partners may mix freely.
 */
export const getOrderTransactionStores = (dwKeys: string[]): OrderTransactionStores => {
  const partnersCountry = dwKeys.length > 0 ? getCountryFromDwKey(dwKeys[0]) : Country.SWEDEN;

  const edwDwKeys = dwKeys.filter(
    (dwKey) => getOrderTransactionStore(dwKey) === OrderTransactionStore.EDW,
  );
  const hasDatalakeOpPartner = dwKeys.some((dwKey) => !isConfidential(dwKey) && isDwkeyOp(dwKey));

  if (hasDatalakeOpPartner && edwDwKeys.length) {
    throw new Error(
      `Mixing Datalake and EDW partners is not allowed. EDW partners: ${JSON.stringify(edwDwKeys)}`,
    );
  }

  const getMix = (): OrderTransactionStoreMixValues => {
    if (edwDwKeys.length === 0) return OrderTransactionStoreMix.ALL_OP;
    if (edwDwKeys.length === dwKeys.length) return OrderTransactionStoreMix.ALL_EDW;

    return OrderTransactionStoreMix.MIXED;
  };

  return {
    mix: getMix(),
    edwDwKeys,
    partnersCountry,
  };
};

/**
 * Routes a query by the stores its partners span. A Record keyed by the mix makes a
 * missed case a type error rather than a silent fall-through to the wrong store.
 */
export const selectByStoreMix = <TAllOp, TMixed, TAllEdw>(
  mix: OrderTransactionStoreMixValues,
  handlers: {
    [OrderTransactionStoreMix.ALL_OP]: () => TAllOp;
    [OrderTransactionStoreMix.MIXED]: () => TMixed;
    [OrderTransactionStoreMix.ALL_EDW]: () => TAllEdw;
  },
): TAllOp | TMixed | TAllEdw => handlers[mix]();
